/**
 * useTeamAdmin — Admin-Funktionen für Team-Verwaltung
 * 
 * Passwort-Reset, Account-Aktivierung/Deaktivierung, Rollen-Änderung
 * Nutzt Supabase Auth Admin API via Service Role Key (nur für Admins)
 */

import { useCallback, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface AdminAction {
  loading: boolean;
  error: string | null;
}

export function useTeamAdmin() {
  const { isAdmin, session } = useAuth();
  const [actionState, setActionState] = useState<AdminAction>({ loading: false, error: null });

  // Mitarbeiter-Daten in der mitarbeiter-Tabelle aktualisieren
  const updateMitarbeiter = useCallback(async (
    mitarbeiterId: string,
    updates: {
      vorname?: string;
      nachname?: string;
      email?: string;
      telefon?: string;
      rolle?: string;
      business_units?: string[];
      ist_aktiv?: boolean;
      avatar_url?: string;
      email_signatur?: string;
    }
  ) => {
    if (!isAdmin) {
      toast.error('Nur Administratoren können Mitarbeiter bearbeiten');
      return false;
    }

    setActionState({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('mitarbeiter')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', mitarbeiterId);

      if (error) throw error;
      
      toast.success('Mitarbeiter aktualisiert');
      setActionState({ loading: false, error: null });
      return true;
    } catch (err: any) {
      const msg = err?.message || 'Fehler beim Aktualisieren';
      toast.error(msg);
      setActionState({ loading: false, error: msg });
      return false;
    }
  }, [isAdmin]);

  // Passwort-Reset-E-Mail senden
  const sendPasswordReset = useCallback(async (email: string) => {
    if (!isAdmin) {
      toast.error('Nur Administratoren können Passwörter zurücksetzen');
      return false;
    }

    setActionState({ loading: true, error: null });
    try {
      // Supabase Auth resetPasswordForEmail funktioniert auch als Admin
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;
      
      toast.success(`Passwort-Reset-E-Mail an ${email} gesendet`);
      setActionState({ loading: false, error: null });
      return true;
    } catch (err: any) {
      const msg = err?.message || 'Fehler beim Senden der Reset-E-Mail';
      toast.error(msg);
      setActionState({ loading: false, error: msg });
      return false;
    }
  }, [isAdmin]);

  // Account aktivieren/deaktivieren
  const toggleAccountStatus = useCallback(async (
    mitarbeiterId: string,
    istAktiv: boolean
  ) => {
    if (!isAdmin) {
      toast.error('Nur Administratoren können Accounts verwalten');
      return false;
    }

    setActionState({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('mitarbeiter')
        .update({ ist_aktiv: istAktiv, updated_at: new Date().toISOString() })
        .eq('id', mitarbeiterId);

      if (error) throw error;
      
      toast.success(istAktiv ? 'Account aktiviert' : 'Account deaktiviert');
      setActionState({ loading: false, error: null });
      return true;
    } catch (err: any) {
      const msg = err?.message || 'Fehler beim Ändern des Account-Status';
      toast.error(msg);
      setActionState({ loading: false, error: msg });
      return false;
    }
  }, [isAdmin]);

  // Rolle ändern
  const changeRole = useCallback(async (
    mitarbeiterId: string,
    newRole: string
  ) => {
    if (!isAdmin) {
      toast.error('Nur Administratoren können Rollen ändern');
      return false;
    }

    setActionState({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('mitarbeiter')
        .update({ rolle: newRole, updated_at: new Date().toISOString() })
        .eq('id', mitarbeiterId);

      if (error) throw error;
      
      toast.success(`Rolle geändert zu: ${newRole}`);
      setActionState({ loading: false, error: null });
      return true;
    } catch (err: any) {
      const msg = err?.message || 'Fehler beim Ändern der Rolle';
      toast.error(msg);
      setActionState({ loading: false, error: msg });
      return false;
    }
  }, [isAdmin]);

  // Betriebe zuweisen
  const updateBusinessUnits = useCallback(async (
    mitarbeiterId: string,
    businessUnits: string[]
  ) => {
    if (!isAdmin) {
      toast.error('Nur Administratoren können Betriebe zuweisen');
      return false;
    }

    setActionState({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('mitarbeiter')
        .update({ business_units: businessUnits, updated_at: new Date().toISOString() })
        .eq('id', mitarbeiterId);

      if (error) throw error;
      
      toast.success('Betriebe aktualisiert');
      setActionState({ loading: false, error: null });
      return true;
    } catch (err: any) {
      const msg = err?.message || 'Fehler beim Aktualisieren der Betriebe';
      toast.error(msg);
      setActionState({ loading: false, error: msg });
      return false;
    }
  }, [isAdmin]);

  // Modul-Berechtigungen aktualisieren
  const updateModulePermissions = useCallback(async (
    mitarbeiterId: string,
    modulePermissions: Record<string, boolean>
  ) => {
    if (!isAdmin) {
      toast.error('Nur Administratoren können Berechtigungen ändern');
      return false;
    }

    setActionState({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('mitarbeiter')
        .update({ module_permissions: modulePermissions, updated_at: new Date().toISOString() })
        .eq('id', mitarbeiterId);

      if (error) throw error;
      
      toast.success('Berechtigungen aktualisiert');
      setActionState({ loading: false, error: null });
      return true;
    } catch (err: any) {
      const msg = err?.message || 'Fehler beim Aktualisieren der Berechtigungen';
      toast.error(msg);
      setActionState({ loading: false, error: msg });
      return false;
    }
  }, [isAdmin]);

  // Passwort direkt setzen (für neue Mitarbeiter)
  const setPassword = useCallback(async (
    email: string,
    newPassword: string
  ) => {
    if (!isAdmin) {
      toast.error('Nur Administratoren können Passwörter setzen');
      return false;
    }

    setActionState({ loading: true, error: null });
    try {
      // Nutze Supabase Auth Admin API über Edge Function oder direkt
      // Da wir keinen Service Role Key im Frontend haben, nutzen wir resetPasswordForEmail
      // Alternativ: Passwort über signUp mit existierender E-Mail setzen
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;
      
      toast.success(`Passwort-Link an ${email} gesendet. Der Mitarbeiter kann damit ein neues Passwort setzen.`);
      setActionState({ loading: false, error: null });
      return true;
    } catch (err: any) {
      const msg = err?.message || 'Fehler beim Setzen des Passworts';
      toast.error(msg);
      setActionState({ loading: false, error: msg });
      return false;
    }
  }, [isAdmin]);

  return {
    ...actionState,
    updateMitarbeiter,
    sendPasswordReset,
    toggleAccountStatus,
    changeRole,
    updateBusinessUnits,
    updateModulePermissions,
    setPassword,
  };
}
